<template>
  <div class="tw-flex tw-flex-col tw-gap-2">
    <label class="wds-label">
      <WdsText>
        {{ fieldTyped.title }}
        <span v-if="required" class="tw-text-indigo-500 tw-font-bold">*</span>
        <WdsTag v-else sm class="tw-ml-1">{{ $t('Optional') }}</WdsTag>
        <CustomFieldVisibilityBadge
          :applies-to="fieldTyped.appliesTo"
          :shared-with-clients="fieldTyped.sharedWithClients"
          :shared-with-staffing-agencies="fieldTyped.sharedWithStaffingAgencies"
          :managed-elsewhere="!fieldTyped.viewerOwnsField"
          :owner-account-name="fieldTyped.account?.name"
        />
      </WdsText>
    </label>

    <ul
      v-if="currentFiles.length > 0"
      class="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-1"
    >
      <li
        v-for="file in currentFiles"
        :key="file.globalId"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <a
          :href="file.downloadUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="tw-text-blue-600 tw-underline"
        >
          {{ file.name }}
        </a>
        <span v-if="file.humanReadableSize" class="tw-text-xs tw-text-gray-500">
          ({{ file.humanReadableSize }})
        </span>
        <WdsButtonIcon
          v-if="!disabled"
          transparent-background
          sm
          :title="$t('Remove file')"
          @click="removeFile(file.globalId)"
        >
          <WdsIcon name="trash-can" :size="14" />
        </WdsButtonIcon>
      </li>
    </ul>

    <div v-if="!disabled">
      <PickerFileFilebank
        v-slot="{ openFilePicker }"
        :exclude-ids="currentFileIds"
        use-global-id
        @files-selected="handleFilesSelected"
      >
        <button
          type="button"
          :class="['btn', currentFiles.length > 0 ? 'btn-default' : 'btn-primary', 'btn-sm']"
          @click="openFilePicker"
        >
          {{ chooseLabel }}
        </button>
      </PickerFileFilebank>
    </div>

    <p
      v-if="fieldTyped.description"
      class="tw-text-base tw-font-circular tw-text-gray-700 tw-mt-1 tw-mb-0"
    >
      {{ fieldTyped.description }}
    </p>

    <p v-if="error" class="tw-text-sm tw-text-red-600 tw-m-0">
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { WdsTag, WdsText, WdsButtonIcon, WdsIcon } from '@worksome/wds-components'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import useIsRequiredField from '@/components/Fieldable/composables/useIsRequiredField'
import { FieldableCustomFieldProps } from '@/components/Fieldable/types'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'

const emits = defineEmits(['update:model-value'])
const props = defineProps<FieldableCustomFieldProps>()

const fieldTyped = computed(() => useFragment(FieldableFormCustomFieldFragment, props.field))
const required = useIsRequiredField(props.field)

const disabled = computed(
  () =>
    props.forceDisabled ||
    (props.forceEnabled ? false : !fieldTyped.value.viewerCanManageFieldValues),
)

interface DisplayFile {
  globalId: string
  name: string
  downloadUrl: string
  humanReadableSize: string | null
}

// Files selected via the bank picker in this session, keyed by globalId.
// Surfaced from this map so newly-attached files render immediately without
// a page refresh — they live on the server but are not yet present in the
// `existingFiles` prop loaded on initial render.
const recentlyPickedById = ref<Record<string, DisplayFile>>({})

const currentFileIds = computed<string[]>(() => {
  const fu = props.modelValue?.fileUpload
  if (!fu) return []
  if (Array.isArray(fu.fileIds)) return fu.fileIds.filter((id): id is string => Boolean(id))
  if (fu.fileId) return [fu.fileId]
  return []
})

const existingById = computed<Record<string, DisplayFile>>(() => {
  const map: Record<string, DisplayFile> = {}
  for (const file of props.existingFiles ?? []) {
    map[file.globalId] = {
      globalId: file.globalId,
      name: file.name,
      downloadUrl: file.downloadUrl,
      humanReadableSize: file.humanReadableSize ?? null,
    }
  }
  return map
})

const currentFiles = computed<DisplayFile[]>(() =>
  currentFileIds.value
    .map((id) => recentlyPickedById.value[id] ?? existingById.value[id] ?? null)
    .filter((file): file is DisplayFile => file !== null),
)

const chooseLabel = computed(() =>
  currentFiles.value.length === 0 ? $t('Choose files') : $t('Add files'),
)

function emitFiles(fileIds: string[]) {
  emits('update:model-value', {
    fileUpload: {
      id: fieldTyped.value.id,
      fileIds,
    },
  } as CustomFieldTypeValueInput)
}

function removeFile(globalId: string) {
  delete recentlyPickedById.value[globalId]
  emitFiles(currentFileIds.value.filter((id) => id !== globalId))
}

/**
 * Bank picker shape — this is what `PickerFileFilebank` emits per file.
 * The picker spreads the underlying `File` GraphQL row into the object plus
 * adds `id`, so we type-narrow defensively.
 */
interface PickedFile {
  globalId?: string
  localId?: number | string
  name?: string
  humanReadableSize?: string | null
}

function handleFilesSelected(selected: PickedFile[]) {
  if (!Array.isArray(selected) || selected.length === 0) {
    return
  }

  const toAdd = selected.filter(
    (file): file is PickedFile & { globalId: string } =>
      typeof file.globalId === 'string' && file.globalId.length > 0,
  )

  if (toAdd.length === 0) {
    return
  }

  for (const file of toAdd) {
    const alreadyKnown =
      Boolean(recentlyPickedById.value[file.globalId]) || Boolean(existingById.value[file.globalId])
    if (!alreadyKnown) {
      recentlyPickedById.value[file.globalId] = {
        globalId: file.globalId,
        name: file.name ?? $t('Attachment'),
        downloadUrl:
          typeof file.localId === 'number' || typeof file.localId === 'string'
            ? laravelNamedRoute(namedRoutes.files.download, { fileId: file.localId })
            : '#',
        humanReadableSize: file.humanReadableSize ?? null,
      }
    }
  }

  // Dedupe and append — a user could re-pick a file that's already attached.
  const merged = Array.from(new Set([...currentFileIds.value, ...toAdd.map((f) => f.globalId)]))
  emitFiles(merged)
}
</script>
